"use strict";
/**
 * The tap pad's outbox (ai/TAP_PAD_PLAN.md). Taps are recorded at the touch,
 * queued, and sent in batches; the server is idempotent on tap id, so a retry
 * after a dropped response is harmless. Unsent taps are mirrored to storage so
 * a reload on stadium cellular does not lose them. They are not secrets; the
 * session token never goes here.
 *
 * Taps the server refuses, or answers as a conflict, are parked, never retried and never
 * dropped, so one bad tap cannot block the game's queue (ai/nba/N8-taps/PLAN.md §19.3, §19a.5).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.TapQueue = exports.browserTapStorage = exports.CONFLICT_REASON = exports.MAX_ACKNOWLEDGED = void 0;
exports.pruneTapStorage = pruneTapStorage;
exports.forgetSharedTapQueues = forgetSharedTapQueues;
exports.estimateClockSample = estimateClockSample;
exports.estimateClockOffset = estimateClockOffset;
const tap_vocabulary_1 = require("../../shared/tap-vocabulary");
/** Statuses that say nothing about the batch itself (session, permission, timeout, rate limit): retried. */
const RETRIED_4XX = new Set([401, 403, 408, 429]);
/** Past this many unconfirmed acknowledgements the excess is reported, never dropped (N8 §19a.4). */
exports.MAX_ACKNOWLEDGED = 20000;
/** The parked reason of a tap whose id the server already holds with different content. */
exports.CONFLICT_REASON = "conflict";
const isAcknowledged = (v) => {
    const a = v;
    return typeof a === "object" && a !== null && typeof a.tap?.tapId === "string" && Number.isSafeInteger(a.publishedSeq) && a.publishedSeq > 0;
};
const isParked = (v) => {
    const p = v;
    return typeof p === "object" && p !== null && typeof p.tap?.tapId === "string" && typeof p.reason === "string";
};
/** The HTTP status of a whole-batch rejection that parks it, or null to retry. */
function parkingStatus(error) {
    const status = error?.statusCode;
    if (typeof status !== "number" || status < 400 || status > 499 || RETRIED_4XX.has(status))
        return null;
    return status;
}
/** localStorage, never throwing: private windows and full quotas degrade to memory only. */
exports.browserTapStorage = {
    load(key) {
        try {
            const raw = window.localStorage.getItem(key);
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed : [];
        }
        catch {
            return [];
        }
    },
    save(key, items) {
        try {
            if (items.length)
                window.localStorage.setItem(key, JSON.stringify(items));
            else
                window.localStorage.removeItem(key);
            return true;
        }
        catch {
            return false; // memory only
        }
    },
};
/**
 * Removes other games' tap-pad keys that hold nothing a queue would load: an empty unsent list, or
 * an acknowledgement list with no valid entry (a confirmed entry is deleted when confirmed, so an
 * emptied list is a fully confirmed one). Never a parked list, an unsent tap or an unconfirmed
 * acknowledgement; content it cannot read is left alone.
 */
function pruneTapStorage(keepKey, store = window.localStorage) {
    try {
        const keys = [];
        for (let i = 0; i < store.length; i++) {
            const k = store.key(i);
            if (k?.startsWith("tap-pad:") && k !== keepKey && !k.startsWith(`${keepKey}:`) && !k.endsWith(":parked"))
                keys.push(k);
        }
        for (const k of keys) {
            let items;
            try {
                items = JSON.parse(store.getItem(k) ?? "null");
            }
            catch {
                continue;
            }
            if (!Array.isArray(items))
                continue;
            if ((k.endsWith(":acked") ? items.filter(isAcknowledged) : items).length === 0)
                store.removeItem(k);
        }
    }
    catch {
        // storage unavailable: nothing to prune
    }
}
const BACKOFF_START_MS = 1000;
const BACKOFF_MAX_MS = 30000;
/** The page's queues, one per storage key (TapQueue.shared). */
const sharedQueues = new Map();
/** What a page reload does to the shared queues; for tests, which share one page. */
function forgetSharedTapQueues() {
    sharedQueues.clear();
}
class TapQueue {
    constructor(key, send, storage, now = Date.now, 
    /** A send unanswered this long fails and frees the queue; safe because tap ids are idempotent (N8 §15). */
    sendTimeoutMs, options = {}) {
        this.key = key;
        this.send = send;
        this.storage = storage;
        this.now = now;
        this.sendTimeoutMs = sendTimeoutMs;
        this.inflight = false;
        this.failures = 0;
        this.nextAttempt = 0;
        /** A retry asked for while a send was in flight, used once when that send fails (ASTRA-REVIEW-162 §2). */
        this.retryAsked = false;
        /** Sends that failed since the pad opened (N8 §3: failures are reported, not lost). */
        this.failedSends = 0;
        /** Tap ids the server answered as clashing with a different tap, this page only. */
        this.clashed = [];
        this.lastError = null;
        /** Refused taps whose parked save failed: kept in pending (a reload re-sends them) but never sent from here. */
        this.unsavedParked = [];
        /** Highest publishedSeq of a complete record the pad has folded. */
        this.confirmedThrough = 0;
        this.pendingSaveFailed = false;
        this.ackedSaveFailed = false;
        this.persistAcked = options.persistAcknowledged === true;
        this.loadStored();
    }
    /**
     * The page's one queue for `key`, for whichever pad opens on it. Two queues on one key overwrite
     * each other's saves; with one, a closed pad's send in flight settles into what the next pad
     * stores. An idle queue whose saves held reloads from storage, as a new one would; a busy one,
     * or one whose saves failed, keeps memory, the truer copy.
     */
    static shared(key, send, storage, now = Date.now, sendTimeoutMs, options = {}) {
        const held = sharedQueues.get(key);
        if (held) {
            held.reopen(send, storage, now, sendTimeoutMs, options);
            return held;
        }
        const q = new TapQueue(key, send, storage, now, sendTimeoutMs, options);
        sharedQueues.set(key, q);
        return q;
    }
    reopen(send, storage, now, sendTimeoutMs, options) {
        const persist = options.persistAcknowledged === true;
        const keepMemory = this.inflight || this.pendingSaveFailed || this.ackedSaveFailed || this.unsavedParked.length > 0;
        this.send = send;
        this.storage = storage;
        this.now = now;
        this.sendTimeoutMs = sendTimeoutMs;
        if (keepMemory) {
            if (persist && !this.persistAcked) {
                // Pad 2 after Pad 1: what an earlier Pad 2 stored, then this page's newer acknowledgements.
                const merged = new Map(this.loadAcked());
                for (const [id, a] of this.acked) {
                    merged.delete(id);
                    merged.set(id, a);
                }
                this.acked = merged;
            }
            this.persistAcked = persist;
            if (this.pendingSaveFailed)
                this.savePending();
            this.saveAcked();
        }
        else {
            this.persistAcked = persist;
            this.loadStored();
            this.clashed.length = 0;
            this.failures = 0;
            this.confirmedThrough = 0;
        }
        this.failedSends = 0; // counted since the pad opened
        this.lastError = null;
        this.retryNow();
    }
    loadStored() {
        this.pending = this.storage.load(this.key);
        this.parkedTaps = this.storage.load(this.parkedKey).filter(isParked);
        this.acked = new Map(this.persistAcked ? this.loadAcked() : []);
    }
    loadAcked() {
        return this.storage.load(this.ackedKey).filter(isAcknowledged).map((a) => [a.tap.tapId, a]);
    }
    /** The last save of the unsent taps failed: they live in memory only, and a reload would lose them. */
    get storageFailing() {
        return this.pendingSaveFailed;
    }
    savePending() {
        this.pendingSaveFailed = !this.storage.save(this.key, this.pending);
    }
    /** An empty map removes the key. */
    saveAcked() {
        if (this.persistAcked)
            this.ackedSaveFailed = !this.storage.save(this.ackedKey, [...this.acked.values()]);
    }
    get parkedKey() {
        return `${this.key}:parked`;
    }
    get ackedKey() {
        return `${this.key}:acked`;
    }
    /** Conflicts not shown as parked: a conflict is counted once, in the parked count (N8 §19c). */
    get conflicts() {
        const parked = this.parkedIds;
        return this.clashed.filter((id) => !parked.has(id));
    }
    get size() {
        return this.pending.length;
    }
    /** Refused taps, oldest first; kept through reloads (those held in memory after a failed save too). */
    get parked() {
        return this.unsavedParked.length ? [...this.parkedTaps, ...this.unsavedParked] : this.parkedTaps;
    }
    /** Ids of every parked tap: the pad skips their local copies, never the server's row under such an id (N8 §19.3, §19c). */
    get parkedIds() {
        return new Set(this.parked.map((p) => p.tap.tapId));
    }
    get acknowledged() {
        return this.acked;
    }
    /** Unconfirmed acknowledgements beyond MAX_ACKNOWLEDGED: kept, but worth showing. */
    get acknowledgedOverCap() {
        return Math.max(0, this.acked.size - exports.MAX_ACKNOWLEDGED);
    }
    /**
     * A complete record through `maxPublishedSeq` was folded: it now holds every tap acknowledged at
     * or below it. Those go, except what `keep` holds back; a later call drops them once it lets go.
     */
    confirmAcknowledged(maxPublishedSeq, keep) {
        this.confirmedThrough = Math.max(this.confirmedThrough, maxPublishedSeq);
        const before = this.acked.size;
        for (const [id, a] of this.acked)
            if (a.publishedSeq <= this.confirmedThrough && !keep?.(a))
                this.acked.delete(id);
        if (this.acked.size !== before)
            this.saveAcked();
    }
    /** The parked taps for copying off the phone, with the queue (league and game) they came from. */
    parkedJson() {
        return JSON.stringify({ queue: this.key, exportedAt: new Date(this.now()).toISOString(), parked: this.parked });
    }
    /** Unsent taps, oldest first (restored ones included, and refused ones held in memory). */
    get unsent() {
        return this.pending;
    }
    add(tap) {
        this.pending.push(tap);
        this.savePending();
        this.retryNow(); // a new tap goes out now, not after the backoff
    }
    /**
     * Clears the backoff's wait (a new tap, back online, back from the background); its failure
     * count stays. Asked during a send, it outlasts that send's failure.
     */
    retryNow() {
        this.nextAttempt = 0;
        this.retryAsked = this.inflight;
    }
    /**
     * Send what is due, batch after batch, until the queue is empty or a send
     * fails. A tap added while a send is in flight goes out as soon as it lands.
     */
    async flush() {
        if (this.inflight)
            return;
        while (this.sendable().length > 0 && this.now() >= this.nextAttempt) {
            if (await this.sendOne())
                continue;
            if (!this.retryAsked)
                return;
            this.retryAsked = false; // the retry asked for during this send: once, past its backoff
            this.nextAttempt = 0;
        }
    }
    async sendOne() {
        this.inflight = true;
        // Each tap carries the attempt that delivers it; stored first, so a reload keeps the count.
        const ids = new Set(this.sendable().slice(0, tap_vocabulary_1.MAX_TAPS_PER_BATCH).map((t) => t.tapId));
        this.pending = this.pending.map((t) => (ids.has(t.tapId) ? { ...t, sendAttempts: (t.sendAttempts ?? 0) + 1 } : t));
        this.savePending();
        const batch = this.pending.filter((t) => ids.has(t.tapId));
        try {
            const { accepted, conflicts, refused = [], published = [] } = await this.withTimeout(this.send(batch));
            const reasons = new Map(refused.map((r) => [r.tapId, r.reason]));
            // Only this batch's ids: an answer naming a held tap must not drop it from pending.
            const done = new Set([...accepted, ...conflicts, ...reasons.keys()].filter((id) => ids.has(id)));
            // A reply that settles nothing must not become a tight resend loop.
            if (!batch.some((t) => done.has(t.tapId)))
                throw new Error("server settled none of the batch");
            this.clashed.push(...conflicts);
            // Stored before the taps leave pending: a reload in between finds them in one or the other.
            this.acknowledge(batch, published);
            // A conflict is the local submission refused: parked like a refusal. The server's own tap
            // under that id is a different tap, learned from the record (§19a.4).
            const clashed = new Set(conflicts);
            const reasonOf = (t) => reasons.get(t.tapId) ?? exports.CONFLICT_REASON;
            this.settled(this.park(batch.filter((t) => reasons.has(t.tapId) || clashed.has(t.tapId)), reasonOf, done));
            return true;
        }
        catch (error) {
            const status = parkingStatus(error);
            if (status !== null) {
                const reason = `HTTP ${status}: ${error.message}`;
                this.settled(this.park(batch, () => reason, new Set(batch.map((t) => t.tapId))));
                return true;
            }
            this.failures += 1;
            this.failedSends += 1;
            this.nextAttempt = this.now() + Math.min(BACKOFF_START_MS * 2 ** (this.failures - 1), BACKOFF_MAX_MS);
            this.lastError = error.message;
            return false;
        }
        finally {
            this.inflight = false;
        }
    }
    /** Pending taps this instance may send: not refused and held in memory. */
    sendable() {
        if (this.unsavedParked.length === 0)
            return this.pending;
        const held = new Set(this.unsavedParked.map((p) => p.tap.tapId));
        return this.pending.filter((t) => !held.has(t.tapId));
    }
    /**
     * Saves the parked list, merged with what another instance stored on the key, before any tap
     * leaves pending. Returns the ids that may leave pending: `done`, minus refused taps whose save
     * failed, plus held ones the save now covers.
     */
    park(taps, reason, done) {
        const out = new Set(done);
        if (taps.length === 0 && this.unsavedParked.length === 0)
            return out;
        const parkedAt = new Date(this.now()).toISOString();
        const fresh = taps.map((tap) => ({ tap, reason: reason(tap), parkedAt }));
        const byId = new Map();
        for (const p of [...this.storage.load(this.parkedKey).filter(isParked), ...this.parkedTaps, ...this.unsavedParked, ...fresh]) {
            if (!byId.has(p.tap.tapId))
                byId.set(p.tap.tapId, p);
        }
        const merged = [...byId.values()];
        if (this.storage.save(this.parkedKey, merged)) {
            for (const p of this.unsavedParked)
                out.add(p.tap.tapId);
            this.parkedTaps = merged;
            this.unsavedParked = [];
        }
        else {
            const known = new Set(this.parked.map((p) => p.tap.tapId));
            const held = fresh.filter((p) => !known.has(p.tap.tapId));
            this.unsavedParked = [...this.unsavedParked, ...held];
            for (const p of held)
                out.delete(p.tap.tapId);
        }
        return out;
    }
    settled(done) {
        this.pending = this.pending.filter((t) => !done.has(t.tapId));
        this.savePending();
        this.failures = 0;
        this.nextAttempt = 0;
        this.retryAsked = false;
        this.lastError = null;
    }
    /** Only this batch's taps: the fold needs each one's payload. */
    acknowledge(batch, published) {
        const byId = new Map(batch.map((t) => [t.tapId, t]));
        let changed = false;
        for (const { tapId, publishedSeq } of published) {
            const tap = byId.get(tapId);
            if (!tap)
                continue;
            this.acked.delete(tapId); // re-inserted as the newest
            if (publishedSeq > this.confirmedThrough)
                this.acked.set(tapId, { tap, publishedSeq });
            changed = true;
        }
        if (changed)
            this.saveAcked();
    }
    withTimeout(sending) {
        const ms = this.sendTimeoutMs;
        if (ms === undefined)
            return sending;
        let timer;
        const expired = new Promise((_, reject) => {
            timer = setTimeout(() => reject(new Error(`no answer in ${ms} ms`)), ms);
        });
        return Promise.race([sending, expired]).finally(() => clearTimeout(timer));
    }
}
exports.TapQueue = TapQueue;
/** As estimateClockOffset, dated at the chosen sample's midpoint (client ms): the offset's true age. */
function estimateClockSample(samples) {
    if (samples.length === 0)
        return null;
    const best = samples.reduce((a, b) => (b.t1 - b.t0 < a.t1 - a.t0 ? b : a));
    return { offsetMs: best.serverMs - (best.t0 + best.t1) / 2, rttMs: best.t1 - best.t0, at: (best.t0 + best.t1) / 2 };
}
/** Offset (server minus client, ms) from the sample with the shortest round trip. */
function estimateClockOffset(samples) {
    if (samples.length === 0)
        return null;
    const best = samples.reduce((a, b) => (b.t1 - b.t0 < a.t1 - a.t0 ? b : a));
    return { offsetMs: best.serverMs - (best.t0 + best.t1) / 2, rttMs: best.t1 - best.t0 };
}
